import { useParams } from 'react-router-dom';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import GroupIcon from '@mui/icons-material/Group';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import BookOpenPageVariantOutlineIcon from 'mdi-material-ui/BookOpenPageVariantOutline';
import Filler from 'components/shared/Layout/Filler';
import { campaignMetricsSelector } from 'features/campaigns/selectors';
import { invoiceMetricsSelector } from 'features/invoice/selectors';
import RouterDrawerListItem from 'layout/components/drawer/RouterDrawerListItem';
import {
  useCanNavigateTo,
  useIsScopedToClient,
  useKampusRoutes,
} from 'routes/hooks';
import { useAppSelector } from 'store/hooks';

export function StandardDrawerListOptions() {
  const { advertiserId: advertiserIdAsParam } = useParams();

  const isScopedToClient = useIsScopedToClient();
  const disabled = !isScopedToClient;

  const canNavigateTo = useCanNavigateTo();

  const { counts: invoiceCounts } =
    useAppSelector(invoiceMetricsSelector) ?? {};
  const { counts: activeCampaignCounts } =
    useAppSelector(campaignMetricsSelector) ?? {};

  const {
    routeServices,
    routeAdvertisers,
    routeCampaigns,
    routeAudiences,
    routeAudienceTaxonomy,
    routeReportingSuite,
    routeCampaignReporting,
    routeBilling,
  } = useKampusRoutes();

  // Retain advertiser ID when navigating between advertiser-scoped routes
  const routeAudiencesWithAdvertiserId =
    advertiserIdAsParam != null ? routeAudienceTaxonomy : routeAudiences;
  const routeReportingSuiteWithAdvertiserId =
    advertiserIdAsParam != null ? routeCampaignReporting : routeReportingSuite;

  return (
    <>
      {canNavigateTo.services && (
        <RouterDrawerListItem
          to={routeServices}
          text="Services"
          icon={<BookOpenPageVariantOutlineIcon />}
          disabled={disabled}
        />
      )}
      {canNavigateTo.advertisers && (
        <RouterDrawerListItem
          to={routeAdvertisers}
          text="Advertisers"
          icon={<FolderOpenOutlinedIcon />}
          disabled={disabled}
        />
      )}
      {canNavigateTo.campaigns && (
        <RouterDrawerListItem
          to={routeCampaigns}
          text="Campaigns"
          notificationNumber={activeCampaignCounts?.countActive}
          icon={<SensorsOutlinedIcon />}
          disabled={disabled}
        />
      )}
      {canNavigateTo.audiences && (
        <RouterDrawerListItem
          to={routeAudiencesWithAdvertiserId}
          matchTo={routeAudiences}
          text="Audiences"
          icon={<GroupIcon />}
          disabled={disabled}
        />
      )}
      <RouterDrawerListItem
        to={routeReportingSuiteWithAdvertiserId}
        matchTo={routeReportingSuite}
        text="Reporting Suite"
        icon={<BarChartOutlinedIcon />}
        disabled={disabled}
      />
      <Filler />
      {canNavigateTo.billing && (
        <>
          <Divider
            sx={{
              borderColor: (theme) => alpha(theme.palette.common.white, 0.12),
            }}
          />
          <RouterDrawerListItem
            to={routeBilling}
            text="Billing"
            icon={<ReceiptOutlinedIcon />}
            notificationNumber={invoiceCounts?.countOverdue}
            disabled={disabled}
          />
        </>
      )}
    </>
  );
}
